import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, gql } from "@apollo/client";
import { ArrowRightLeft, FileText, ExternalLink, ScanLine, Plus, Save, Pencil, Printer } from "lucide-react";
import { TabHeader } from "./shared/components/TabHeader";
import { TabTable } from "./shared/components/TabTable";
import { Button } from "@/components/ui/button";
import { isoDateToMDY } from "@/utils/common";
import { ScanPreview } from "./scans/modals/PreviewScan";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { FormView } from "../participantDocuments/lib/formView";
import { FormInput } from "../participantDocuments/lib/formRender";
import { DocumentEntryForm } from "./add_entry/components/DocumentEntryForm";
import { EditScans } from "./scans/modals/EditScans";
import { useUserPermissions } from "@/store/useUserPermissions";
import { useUser } from "@/store/useUser";
import { hasAnyPolicy } from "@/utils/policies";
import { toast } from "@/components/ui/toast";
import {
  DocumentTransferDialog,
  TransferableDocument,
} from "./documents/DocumentTransferDialog";

const GET_PARTICIPANT_DOCUMENTS = gql`
  query GetParticipantDocumentsList($odrnumber: Int!) {
    getParticipantByODRNumber(odrnumber: $odrnumber) {
      documents {
        id
        type
        date
        tags
        template
        contents
      }
    }
  }
`;

const UPDATE_PARTICIPANT_DOCUMENT = gql`
  mutation UpdateParticipantDocument($id: Int!, $updates: GenericJSONDocumentInput!) {
    updateParticipantDocument(id: $id, updates: $updates) {
      id
    }
  }
`;

type CombinedItem = {
  id: string;
  source: "document" | "scan";
  date: string | null | undefined;
  label: string;
  typeLabel: string;
  tags?: string[];
  raw: any;
};

const TYPE_LABELS: Record<string, string> = {
  courtfile: "Court Document",
  otherfile: "Other",
  rapsheet: "Rap Sheet",
};

const SCAN_TYPE_ORDER = ["courtfile", "rapsheet", "otherfile"];
const DOCUMENT_TYPE_ORDER = ["Care Plan", "Initial Assessment", "LACare Referral"];
const FILTER_STORAGE_KEY = "documentsScansFilters";
const DEFAULT_SELECTED_TYPES = ["courtfile", "rapsheet", "otherfile"];
const READ_ONLY_DOCUMENT_TYPES = new Set([
  "LACAREREF",
  "LACARE_Initial_Assessment",
  "LACARE_Care_Plan",
  "LACAREProgram_Completion",
]);

const BASE_PALETTE: Record<string, string> = {
  courtfile: "bg-blue-50 text-blue-700 border-blue-200",
  rapsheet: "bg-amber-50 text-amber-800 border-amber-200",
  otherfile: "bg-slate-50 text-slate-700 border-slate-200",
  careplan: "bg-blue-50 text-blue-700 border-blue-200",
  initialassessment: "bg-indigo-50 text-indigo-700 border-indigo-200",
  lacarereferral: "bg-teal-50 text-teal-700 border-teal-200",
};

const FALLBACK_COLORS = [
  "bg-emerald-50 text-emerald-800 border-emerald-200",
  "bg-rose-50 text-rose-700 border-rose-200",
  "bg-cyan-50 text-cyan-700 border-cyan-200",
  "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
  "bg-lime-50 text-lime-700 border-lime-200",
  "bg-orange-50 text-orange-700 border-orange-200",
  "bg-yellow-50 text-yellow-700 border-yellow-200",
];

const normalizeType = (type: string | null | undefined) =>
  (type || "").toLowerCase().replace(/[\s_-]/g, "");

const parseJsonSafe = (value: string | null | undefined): unknown => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (err) {
    console.error("Failed to parse document payload", err);
    return null;
  }
};

const parseTemplateSafe = (value: string | null | undefined): any[] => {
  const parsed = parseJsonSafe(value);
  return Array.isArray(parsed) ? parsed : [];
};

const parseContentsSafe = (
  value: string | null | undefined
): Record<string, unknown> => {
  const parsed = parseJsonSafe(value);
  return parsed && typeof parsed === "object" && !Array.isArray(parsed)
    ? parsed as Record<string, unknown>
    : {};
};

export const DocumentsScans = ({
  odrnumber,
  scans,
  loading,
  error,
}: {
  odrnumber: number;
  scans: any[];
  loading: boolean;
  error: any;
}) => {
  const { user } = useUser();
  const { policies } = useUserPermissions();
  const canEditDocument = hasAnyPolicy(policies, [
    "program:participant:documentscans~full",
  ]);
  const canManageScans = hasAnyPolicy(policies, [
    "program:participant:documentscans~full",
    "program:participant:scans~full",
  ]);
  const canTransferDocuments = hasAnyPolicy(policies, ["admin:access~full"]);
  const { data, loading: docsLoading, error: docsError, refetch: refetchDocuments } = useQuery(
    GET_PARTICIPANT_DOCUMENTS,
    { variables: { odrnumber } }
  );
  const [updateDocument] = useMutation(UPDATE_PARTICIPANT_DOCUMENT);

  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    try {
      const saved = localStorage.getItem(FILTER_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.types && Array.isArray(parsed.types)) {
          parsed.types.forEach((v: any) => {
            if (typeof v === "string") initial.add(v);
          });
        }
      }
    } catch (err) {
      console.error("Failed to load documents/scans filters", err);
    }
    if (initial.size === 0) {
      DEFAULT_SELECTED_TYPES.forEach((t) => initial.add(t));
    }
    return initial;
  });
  const [selectedScanId, setSelectedScanId] = useState<number | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [editingDocument, setEditingDocument] = useState(false);
  const [editDocumentState, setEditDocumentState] = useState<any>({});
  const [editScansOpen, setEditScansOpen] = useState(false);
  const [newDocumentModal, setNewDocumentModal] = useState(false);
  const [selectedDocumentKeys, setSelectedDocumentKeys] = useState<string[]>([]);
  const [transferDocumentsOpen, setTransferDocumentsOpen] = useState(false);
  const [printingDocument, setPrintingDocument] = useState(false);

  const selectedDocumentId = `${selectedDocument?.id || ""}`;
  const selectedDocumentTemplate = parseTemplateSafe(selectedDocument?.template);
  const selectedDocumentContents = parseContentsSafe(selectedDocument?.contents);
  const selectedDocumentIsEditable = Boolean(
    selectedDocument &&
      /^\d+$/.test(selectedDocumentId) &&
      !READ_ONLY_DOCUMENT_TYPES.has(selectedDocument.type) &&
      selectedDocumentTemplate.length > 0
  );

  const documents = data?.getParticipantByODRNumber?.documents || [];
  const documentSelectionKey = (document: TransferableDocument) =>
    `${document.type || "Document"}:${document.id}`;
  const documentSelectionControlId = (document: TransferableDocument) =>
    `participant-documents-scans-select-${normalizeType(document.type)}-${document.id}`;
  const selectedDocuments: TransferableDocument[] = documents.filter((document: TransferableDocument) =>
    selectedDocumentKeys.includes(documentSelectionKey(document))
  );

  useEffect(() => {
    setSelectedDocumentKeys([]);
    setTransferDocumentsOpen(false);
    setPrintingDocument(false);
  }, [odrnumber]);

  useEffect(() => {
    if (!printingDocument) return;
    const frame = window.requestAnimationFrame(() => {
      window.print();
      setPrintingDocument(false);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [printingDocument]);

  const combinedItems = useMemo<CombinedItem[]>(() => {
    const scanItems: CombinedItem[] = (scans || []).map((scan: any) => {
      const normalized = normalizeType(scan.filetype);
      const label =
        TYPE_LABELS[normalized] ||
        scan.filelabel ||
        scan.filename ||
        "Untitled";

      return {
        id: `scan-${scan.id}`,
        source: "scan",
        date: scan.filedate,
        label,
        typeLabel: TYPE_LABELS[normalized] || scan.filetype || "Scan",
        raw: scan,
      };
    });

    const docItems: CombinedItem[] = documents.map((doc: any) => ({
      id: `doc-${doc.id}`,
      source: "document",
      date: doc.date,
      label: doc.type || "Document",
      typeLabel: doc.type || "Document",
      tags: Array.isArray(doc.tags) ? doc.tags : [],
      raw: doc,
    }));

    return [...scanItems, ...docItems].sort((a, b) => {
      const aDate = a.date ? new Date(a.date).getTime() : 0;
      const bDate = b.date ? new Date(b.date).getTime() : 0;
      return bDate - aDate;
    });
  }, [documents, scans]);

  const availableScanTypes = useMemo(() => {
    const types = new Set<string>();
    (scans || []).forEach((scan: any) => {
      const normalized = normalizeType(scan.filetype);
      if (!normalized) return;
      types.add(normalized);
    });
    return Array.from(types);
  }, [scans]);

  const availableDocumentTypes = useMemo(() => {
    const types = new Set<string>();
    documents.forEach((doc: any) => {
      if (!doc?.type) return;
      types.add(doc.type);
    });
    return Array.from(types);
  }, [documents]);

  useEffect(() => {
    try {
      localStorage.setItem(
        FILTER_STORAGE_KEY,
        JSON.stringify({
          types: Array.from(selectedTypes),
        })
      );
    } catch (err) {
      console.error("Failed to save documents/scans filters", err);
    }
  }, [selectedTypes]);

  const typeFilteredItems = combinedItems.filter((item) => {
    if (selectedTypes.size === 0) return false;
    const typeKey =
      item.source === "scan"
        ? normalizeType(item.raw?.filetype)
        : item.raw?.type;
    if (!typeKey) return false;
    return selectedTypes.has(typeKey);
  });

  const combinedLoading = loading || docsLoading;
  const combinedError = error || docsError;

  const availableTypesOrdered = [
    ...SCAN_TYPE_ORDER.filter((t) => availableScanTypes.includes(t)),
    ...DOCUMENT_TYPE_ORDER.filter((t) => availableDocumentTypes.includes(t)),
    ...availableScanTypes.filter((t) => !SCAN_TYPE_ORDER.includes(t)),
    ...availableDocumentTypes.filter((t) => !DOCUMENT_TYPE_ORDER.includes(t)),
  ];

  const paletteMap = useMemo(() => {
    const map: Record<string, string> = { ...BASE_PALETTE };
    const typeKeys = availableTypesOrdered.map((t) => normalizeType(t) || t);
    typeKeys.forEach((key, idx) => {
      if (map[key]) return;
      const color = FALLBACK_COLORS[idx % FALLBACK_COLORS.length];
      map[key] = color;
    });
    return map;
  }, [availableTypesOrdered]);

  const paletteForType = (type?: string | null) => {
    if (!type) return "bg-gray-50 text-gray-700 border-gray-200";
    const key = normalizeType(type);
    if (paletteMap[key]) return paletteMap[key];
    if (paletteMap[type]) return paletteMap[type];
    return "bg-gray-50 text-gray-700 border-gray-200";
  };

  const openNewDocument = () => {
    if (!canEditDocument) return;
    setNewDocumentModal(true);
  };

  const toggleDocumentSelection = (document: TransferableDocument, checked: boolean) => {
    const selectionKey = documentSelectionKey(document);
    if (checked) {
      setSelectedDocumentKeys((current) => Array.from(new Set([...current, selectionKey])));
      return;
    }
    setSelectedDocumentKeys((current) => current.filter((key) => key !== selectionKey));
  };

  const handleDocumentsTransferred = async () => {
    setSelectedDocumentKeys([]);
    await refetchDocuments();
  };

  const startEditingDocument = (event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (!selectedDocumentIsEditable) return;
    setEditDocumentState(selectedDocumentContents);
    setEditingDocument(true);
  };

  const handleUpdateDocument = async () => {
    if (!selectedDocumentIsEditable) return;

    try {
      await updateDocument({
        variables: {
          id: Number.parseInt(selectedDocumentId, 10),
          updates: {
            contents: JSON.stringify(editDocumentState || {}),
            published: 0,
            metadata: "{}",
            type: selectedDocument.type,
            odrparticipant: odrnumber,
            template: selectedDocument.template,
          },
        },
      });
      toast.success("Document updated.");
      setEditingDocument(false);
      setSelectedDocument(null);
      await refetchDocuments();
    } catch (updateError: any) {
      toast.error(updateError?.message || "Unable to update document.");
    }
  };

  const renderTypeBadge = (item: CombinedItem) => {
    const color = item.source === "scan"
      ? paletteForType(item.raw?.filetype)
      : paletteForType(item.raw?.type);
    return (
      <span className={`inline-flex items-center rounded-md border px-2 py-1 text-xs font-medium ${color}`}>
        {item.typeLabel}
      </span>
    );
  };

  if (combinedLoading) return <div className="p-6 text-sm text-gray-500">Loading documents and scans...</div>;
  if (combinedError) return <div className="p-6 text-sm text-red-600">Error loading documents/scans.</div>;
  if (printingDocument && selectedDocument) {
    return (
      <FormView
        print
        formTemplate={selectedDocumentTemplate}
        formState={selectedDocumentContents}
      />
    );
  }

  return (
    <div className="p-6 h-full overflow-y-auto space-y-4 bg-white">
      <TabHeader
        icon={FileText}
        title="Documents / Scans"
        subtitle="Unified list of documents and scanned files"
        action={(canEditDocument || canManageScans || canTransferDocuments) ? (
          <div className="flex items-center gap-3">
            {canTransferDocuments && (
              <Button
                id="participant-documents-scans-transfer-selected"
                size="sm"
                variant="outline"
                className="h-8 gap-1.5"
                disabled={selectedDocuments.length === 0}
                onClick={() => setTransferDocumentsOpen(true)}
              >
                <ArrowRightLeft className="h-3.5 w-3.5" />
                Transfer{selectedDocuments.length > 0 ? ` (${selectedDocuments.length})` : ""}
              </Button>
            )}
            {canTransferDocuments && (canEditDocument || canManageScans) && (
              <div className="h-4 w-px bg-gray-200 mx-1" />
            )}
            {canEditDocument && (
              <Button
                id="participant-documents-scans-add-document"
                size="sm"
                className="h-8 gap-1.5 shadow-sm"
                onClick={openNewDocument}
              >
                <Plus className="h-3.5 w-3.5" />
                Add Document
              </Button>
            )}
            {canEditDocument && canManageScans && (
              <div className="h-4 w-px bg-gray-200 mx-1" />
            )}
            {canManageScans && (
              <Button
                id="participant-documents-scans-manage-scans"
                size="sm"
                variant="outline"
                className="h-8 gap-1.5"
                onClick={() => setEditScansOpen(true)}
              >
                <ScanLine className="h-3.5 w-3.5" />
                Manage Scans
              </Button>
            )}
          </div>
        ) : undefined}
      />

      <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center gap-2 flex-wrap">
        {availableTypesOrdered.map((t) => {
          const isOn = selectedTypes.has(t);
          const label =
            TYPE_LABELS[normalizeType(t)] ||
            TYPE_LABELS[t] ||
            t;
          const palette = paletteForType(t);
          return (
            <button
              key={t}
              onClick={() => {
                const next = new Set(selectedTypes);
                if (isOn) {
                  next.delete(t);
                }
                if (!isOn) {
                  next.add(t);
                }
                if (next.size === 0) {
                  DEFAULT_SELECTED_TYPES.forEach((d) => next.add(d));
                }
                setSelectedTypes(next);
              }}
              className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border transition-all select-none ${isOn ? palette : "bg-white text-gray-400 border-gray-100 hover:bg-gray-50"}`}
            >
              {label}
            </button>
          );
        })}
      </div>

      <div className="border-t border-b border-gray-200">
        <TabTable<CombinedItem>
          data={typeFilteredItems}
          emptyMessage="No documents or scans found."
          columns={[
            ...(canTransferDocuments ? [{
              title: "Select",
              className: "w-[80px]",
              render: (item: CombinedItem) => {
                if (item.source !== "document") return null;
                const selectionKey = documentSelectionKey(item.raw);
                return (
                  <input
                    id={documentSelectionControlId(item.raw)}
                    aria-label={`Select ${item.label}`}
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300"
                    checked={selectedDocumentKeys.includes(selectionKey)}
                    onChange={(event) => toggleDocumentSelection(item.raw, event.target.checked)}
                  />
                );
              },
            }] : []),
            {
              title: "Date",
              className: "w-[140px]",
              render: (item) => (item.date ? isoDateToMDY(item.date) : "—"),
            },
            {
              title: "Title",
              render: (item) => (
                <div className="space-y-1">
                  <div className="text-sm text-gray-900">{item.label}</div>
                  {item.source === "document" && item.tags && item.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {item.tags.map((tag: string) => (
                        <span
                          key={tag}
                          className="inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium text-blue-700 border-blue-200 bg-white"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ),
            },
            {
              title: "Type",
              className: "w-[180px]",
              render: renderTypeBadge,
            },
            {
              title: "Source",
              className: "w-[110px]",
              render: (item) => (
                <span className="inline-flex items-center gap-1 text-xs text-gray-700">
                  {item.source === "scan" ? <ScanLine className="h-3.5 w-3.5" /> : <FileText className="h-3.5 w-3.5" />}
                  {item.source === "scan" ? "Scan" : "Document"}
                </span>
              ),
            },
            {
              title: "Actions",
              className: "w-[120px]",
              render: (item) => {
                if (item.source === "scan") {
                  return (
                    <button
                      className="inline-flex items-center gap-1 text-blue-600 text-sm hover:underline"
                      onClick={() => setSelectedScanId(item.raw.id)}
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      View
                    </button>
                  );
                }

                return (
                  <button
                    className="inline-flex items-center gap-1 text-blue-600 text-sm hover:underline"
                    onClick={() => setSelectedDocument(item.raw)}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    View
                  </button>
                );
              },
            },
          ]}
        />
      </div>

      {selectedScanId && (
        <ScanPreview
          odrnumber={odrnumber}
          scan={selectedScanId}
          close={() => setSelectedScanId(null)}
        />
      )}

      <Dialog
        open={Boolean(selectedDocument)}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setSelectedDocument(null);
            setEditingDocument(false);
          }
        }}
      >
        <DialogContent 
          className="sm:max-w-[80%] w-[80%] h-[90%] flex flex-col p-0 gap-0"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <div className="flex-none p-4 border-b flex items-center justify-between bg-white">
            <div className="space-y-1">
              <h2 className="text-lg font-semibold text-gray-800">
                {editingDocument ? `Edit ${selectedDocument?.type || "Document"}` : selectedDocument?.type || "View Document"}
              </h2>
              {Array.isArray(selectedDocument?.tags) && selectedDocument.tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {selectedDocument.tags.map((tag: string) => (
                    <span
                      key={tag}
                      className="inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium text-blue-700 border-blue-200 bg-white"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto bg-white p-6">
            {selectedDocumentTemplate.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-gray-500">
                This document has no form template to display.
              </div>
            ) : editingDocument ? (
              <FormInput
                formId="participant-document-edit-form"
                formTemplate={selectedDocumentTemplate}
                formState={editDocumentState}
                setFormState={setEditDocumentState}
                onSubmit={handleUpdateDocument}
                onClose={() => setEditingDocument(false)}
                unboxed
              />
            ) : (
              <FormView
                formTemplate={selectedDocumentTemplate}
                formState={selectedDocumentContents}
                unboxed
              />
            )}
          </div>
          <div className="flex-none p-4 border-t flex items-center justify-between bg-gray-50 rounded-b-lg">
            {editingDocument ? (
              <>
                <Button
                  type="submit"
                  form="participant-document-edit-form"
                  size="sm"
                  className="gap-1.5 shadow-sm"
                >
                  <Save className="h-4 w-4" />
                  Save Changes
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setEditingDocument(false)}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  {selectedDocumentTemplate.length > 0 && (
                    <Button
                      id="participant-documents-scans-print"
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => setPrintingDocument(true)}
                    >
                      <Printer className="h-4 w-4" />
                      Print / Save PDF
                    </Button>
                  )}
                  {canEditDocument && selectedDocumentIsEditable && (
                    <Button
                      id="participant-documents-scans-edit"
                      type="button"
                      size="sm"
                      className="gap-1.5 shadow-sm"
                      onClick={startEditingDocument}
                    >
                      <Pencil className="h-4 w-4" />
                      Edit Document
                    </Button>
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="ml-auto"
                  onClick={() => setSelectedDocument(null)}
                >
                  Close
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={newDocumentModal}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setNewDocumentModal(false);
          }
        }}
      >
        <DialogContent 
          contentWidth="1100px"
          contentMaxWidth="95vw"
          contentHeight="auto"
          contentMaxHeight="90vh"
          className="overflow-y-auto !gap-2 !p-4 !pt-4"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <h2 className="text-xl font-bold">Add Document</h2>
          <p className="text-sm text-[var(--neutral-gray-100)]">
            Complete the document details below and save to update the participant record.
          </p>
          <DocumentEntryForm
            odrNumber={odrnumber.toString()}
            user={user}
            showHeader={false}
            onSuccess={() => setNewDocumentModal(false)}
            onCancel={() => setNewDocumentModal(false)}
            refetch={refetchDocuments}
          />
        </DialogContent>
      </Dialog>

      {canManageScans && editScansOpen && (
        <EditScans
          isOpen={editScansOpen}
          onClose={() => setEditScansOpen(false)}
          scans={scans}
          odrNumber={odrnumber}
          programNumber={1}
        />
      )}

      {canTransferDocuments && transferDocumentsOpen && (
        <DocumentTransferDialog
          open={transferDocumentsOpen}
          currentOdrNumber={odrnumber}
          documents={selectedDocuments}
          onClose={() => setTransferDocumentsOpen(false)}
          onTransferred={handleDocumentsTransferred}
        />
      )}
    </div>
  );
};

export default DocumentsScans;
